import {
  Connection,
  InviteUserPayload,
  OrganizationUser,
  PendingInvite,
  Role,
} from "@cohesive/api-client";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "./api";

export function useUsers(
  options?: Partial<UseQueryOptions<OrganizationUser[], AxiosError>>,
) {
  return useQuery<OrganizationUser[], AxiosError>({
    queryKey: ["users"],
    queryFn: () => orgAPI.users.list(),
    ...options,
  });
}

// React Query Hooks
export function useRoles(options?: UseQueryOptions<Role[], AxiosError>) {
  return useQuery<Role[], AxiosError>({
    queryKey: ["roles"],
    queryFn: () => orgAPI.users.listRoles(),
    placeholderData: [],
    ...options,
  });
}

export function useInviteUser(
  options?: UseMutationOptions<void, AxiosError, InviteUserPayload>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, InviteUserPayload>({
    mutationFn: (payload) => orgAPI.invites.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingInvites"] });
    },
    ...options,
  });
}

export function usePendingInvites(
  options?: Partial<UseQueryOptions<PendingInvite[], AxiosError>>,
) {
  return useQuery<PendingInvite[], AxiosError>({
    queryKey: ["pendingInvites"],
    queryFn: () => orgAPI.invites.list(),
    ...options,
  });
}

export function useOrgUsers(
  options?: Partial<UseQueryOptions<OrganizationUser[], AxiosError>>,
) {
  return useQuery<OrganizationUser[], AxiosError>({
    queryKey: ["users"],
    queryFn: () => orgAPI.users.list(),
    ...options,
  });
}

export function useUpdateUser(
  userId: string,
  options?: UseMutationOptions<
    OrganizationUser,
    AxiosError,
    Partial<OrganizationUser>
  >,
) {
  const queryClient = useQueryClient();
  return useMutation<OrganizationUser, AxiosError, Partial<OrganizationUser>>({
    mutationFn: (userData) => orgAPI.users.update(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    ...options,
  });
}

export function useDeleteUser(
  userId: string,
  options?: UseMutationOptions<void, AxiosError>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError>({
    mutationFn: () => orgAPI.users.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    ...options,
  });
}

export function useDeletePendingInvite(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, string>({
    mutationFn: (invitationId) => orgAPI.invites.delete(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingInvites"] });
    },
    ...options,
  });
}

export function useConnections(
  options?: UseQueryOptions<Connection[], AxiosError>,
) {
  return useQuery<Connection[], AxiosError>({
    queryKey: ["connections"],
    queryFn: () => orgAPI.organization.listConnections(),
    ...options,
  });
}
